import React, { useEffect, useState } from "react";
import { GetUser } from "../../Services";
import { useSelector } from "react-redux";
import {
  FaUserEdit,
  FaSave,
  FaTimes,
  FaLock,
  FaUserShield,
} from "react-icons/fa";

const AdminProfileView = () => {
  const adminId = useSelector((state) => state.auth.user.id);

  const [admin, setAdmin] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordMessage, setPasswordMessage] = useState("");

  // 🔹 Fetch admin
  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const res = await GetUser.fetchUsersById(adminId);
        setAdmin(res);
      } catch {
        setMessage("Failed to load admin profile.");
      }
    };
    fetchAdmin();
  }, [adminId]);

  // 🔹 Profile input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdmin((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Update profile
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const updateData = {
        name: admin.name,
        email: admin.email,
        phone: admin.phone,
      };
      const res = await GetUser.ProfileUpdate(adminId, updateData);
      setAdmin(res);
      setEditMode(false);
      setMessage("Profile updated successfully!");
    } catch {
      setMessage("Error updating profile.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Password
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage("New passwords do not match.");
      return;
    }

    try {
      await GetUser.PasswordUpdate(adminId, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordMessage("Password changed successfully!");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch {
      setPasswordMessage("Error changing password.");
    }
  };

  if (!admin) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <div className="container mt-4 mb-5">
      {/* PROFILE HEADER */}
      <div className="card shadow border-0 mb-4">
        <div
          className="card-body text-center text-white"
          style={{
            background: "linear-gradient(135deg, #0d6efd, #0a58ca)",
            borderRadius: "0.5rem",
          }}
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
            alt="Admin"
            className="rounded-circle border border-3 border-light mb-3"
            width="120"
            height="120"
          />
          <h3 className="mb-0">{admin.name || "Admin User"}</h3>
          <p className="mb-1 opacity-75">{admin.email}</p>

          <span className="badge bg-light text-primary px-3 py-2 mt-2">
            <FaUserShield className="me-1" /> {admin.role}
          </span>
        </div>
      </div>

      {/* ALERTS */}
      {message && (
        <div
          className={`alert ${message.includes("Error") ? "alert-danger" : "alert-success"}`}
        >
          {message}
        </div>
      )}

      {/* PROFILE DETAILS */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-white fw-bold">Profile Information</div>
        <div className="card-body">
          {!editMode ? (
            <div className="row g-3">
              <Info label="Phone" value={admin.phone} />
              <Info
                label="Status"
                value={
                  admin.status ? (
                    <span className="badge bg-success">Active</span>
                  ) : (
                    <span className="badge bg-secondary">Inactive</span>
                  )
                }
              />
              <Info
                label="Created At"
                value={new Date(admin.createdAt).toLocaleString()}
              />

              <div className="col-12 text-end">
                <button
                  className="btn btn-primary"
                  onClick={() => setEditMode(true)}
                >
                  <FaUserEdit className="me-2" /> Edit Profile
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleUpdate} className="row g-3">
              <Input
                label="Name"
                name="name"
                value={admin.name}
                onChange={handleChange}
              />
              <Input
                label="Email"
                name="email"
                value={admin.email}
                onChange={handleChange}
              />
              <Input
                label="Phone"
                name="phone"
                value={admin.phone}
                onChange={handleChange}
              />

              <div className="col-12 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditMode(false)}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                >
                  <FaSave /> {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* SECURITY */}
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white fw-bold">
          <FaLock className="me-2" /> Security
        </div>
        <div className="card-body">
          {passwordMessage && (
            <div
              className={`alert ${
                passwordMessage.includes("Error") ||
                passwordMessage.includes("match")
                  ? "alert-danger"
                  : "alert-success"
              }`}
            >
              {passwordMessage}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="row g-3">
            <Password
              label="Current Password"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
            />
            <Password
              label="New Password"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
            />
            <Password
              label="Confirm Password"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
            />

            <div className="col-12 text-end">
              <button type="submit" className="btn btn-warning">
                Change Password
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// 🔹 Small components
const Info = ({ label, value }) => (
  <div className="col-md-6">
    <label className="text-muted small">{label}</label>
    <div className="fw-semibold">{value || "—"}</div>
  </div>
);

const Input = ({ label, ...props }) => (
  <div className="col-md-6">
    <label className="form-label">{label}</label>
    <input className="form-control" {...props} />
  </div>
);

const Password = ({ label, ...props }) => (
  <div className="col-md-4">
    <label className="form-label">{label}</label>
    <input type="password" className="form-control" {...props} required />
  </div>
);

export default AdminProfileView;
